import React, { createContext, useContext, useReducer } from "react";

const INITIAL_STATE = {
  reserveCategories: [],
  incomes: [],
  awaitingExpenses: [],
  completedExpenses: [],
};

const FinanceContext = createContext();

function computeDerived(state) {
  const totalReserve = state.reserveCategories.reduce((s, c) => s + c.amount, 0);
  const totalIncome = state.incomes.reduce((s, i) => s + i.amount, 0);
  const totalAllocated = state.incomes.reduce((s, i) => {
    return s + (i.allocated || []).reduce((a, al) => a + al.amount, 0);
  }, 0);
  const generatingIncome = totalIncome - totalAllocated;
  const totalCompleted = state.completedExpenses.reduce((s, e) => s + e.amount, 0);
  const totalAwaiting = state.awaitingExpenses.reduce((s, e) => s + e.amount, 0);
  return { totalReserve, totalIncome, generatingIncome, totalCompleted, totalAwaiting };
}

function reducer(state, action) {
  switch (action.type) {
    case "ADD_RESERVE_CATEGORY":
      return {
        ...state,
        reserveCategories: [
          ...state.reserveCategories,
          {
            id: Date.now(),
            name: action.payload.name,
            // ✅ NEW: accept amount from payload (default 0)
            amount: parseFloat(action.payload.amount) || 0,
            type: action.payload.catType,
          },
        ],
      };

    case "ADD_INCOME":
      return {
        ...state,
        incomes: [...state.incomes, { id: Date.now(), ...action.payload, allocated: action.payload.allocated || [] }],
      };

    case "ADD_EXPENSE":
      return {
        ...state,
        awaitingExpenses: [...state.awaitingExpenses, { id: Date.now(), ...action.payload }],
      };

    case "COMPLETE_EXPENSE": {
      const expense = state.awaitingExpenses.find((e) => e.id === action.payload);
      if (!expense) return state;
      return {
        ...state,
        awaitingExpenses: state.awaitingExpenses.filter((e) => e.id !== action.payload),
        completedExpenses: [
          ...state.completedExpenses,
          { ...expense, date: new Date().toISOString().split("T")[0] },
        ],
      };
    }

    case "ADD_SPEND": {
      const { category, amount, purpose, date } = action.payload;
      return {
        ...state,
        awaitingExpenses: [
          ...state.awaitingExpenses,
          { id: Date.now(), label: purpose, amount, category, dueDate: date, type: "manual" },
        ],
      };
    }

    case "ADD_FUNDS": {
  const { category, amount, purpose, allocated = [] } = action.payload;
  const chargesAmount = allocated.reduce((s, al) => s + al.amount, 0);

  const newAwaiting = chargesAmount > 0
    ? [
        ...state.awaitingExpenses,
        {
          id: Date.now() + 1,
          label: "Charges – " + purpose,
          amount: chargesAmount,
          category,
          dueDate: new Date().toISOString().split("T")[0],
          type: "charges",
        },
      ]
    : state.awaitingExpenses;

  return {
    ...state,
    incomes: [
      ...state.incomes,
      {
        id: Date.now(),
        amount,
        purpose,
        office: category,
        date: new Date().toISOString().split("T")[0],
        allocated,
      },
    ],
    awaitingExpenses: newAwaiting,
  };
}

    case "EDIT_EXPENSE": {
      const { id, label, amount, category, dueDate } = action.payload;
      return {
        ...state,
        awaitingExpenses: state.awaitingExpenses.map((e) =>
          e.id === id ? { ...e, label, amount, category, dueDate } : e
        ),
      };
    }

    case "DELETE_EXPENSE": {
      return {
        ...state,
        awaitingExpenses: state.awaitingExpenses.filter((e) => e.id !== action.payload),
      };
    }

    case "MOVE_UNSPENT_TO_RESERVE": {
      const derived = computeDerived(state);
      const perCat = derived.generatingIncome / state.reserveCategories.length;
      return {
        ...state,
        reserveCategories: state.reserveCategories.map((c) => ({ ...c, amount: c.amount + perCat })),
      };
    }

    default:
      return state;
  }
}

export function FinanceProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const derived = computeDerived(state);
  return (
    <FinanceContext.Provider value={{ state, dispatch, derived }}>
      {children}
    </FinanceContext.Provider>
  );
}

export function useFinance() {
  return useContext(FinanceContext);
}